import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import complianceAuditService from '../../../services/compliance/complianceAudit.service';
import type { ComplianceAuditLogEntry } from '../../../types/compliance';
import { useUserNames } from '../../../hooks/useUserNames';


interface Props {
  /** Server-side entity_type filter (e.g. "contract", "vendor", "third_party"). */
  entityType: string;
  /** Server-side entity_id filter. */
  entityId: string;
  /** Row cap. Default 25 — deep histories should paginate via the full
   *  audit page. */
  limit?: number;
  /** Optional heading override. Defaults to "Audit History". */
  title?: string;
}


function describe(entry: ComplianceAuditLogEntry): string {
  // Strip the leading "compliance." to keep the inline log compact;
  // the full audit page has the canonical action column anyway.
  return entry.action.replace(/^compliance\./, '');
}


export default function EntityAuditPanel({
  entityType,
  entityId,
  limit = 25,
  title = 'Audit History',
}: Props) {
  const { nameFor } = useUserNames();
  const [rows, setRows] = useState<ComplianceAuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await complianceAuditService.list({
        entity_type: entityType,
        entity_id: entityId,
        limit,
      });
      setRows(data);
    } catch (error: any) {
      setErr(
        error?.response?.data?.detail || error?.message || 'Unknown error',
      );
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, limit]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-panel overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 text-sm">
        <span className="font-semibold text-gray-700 dark:text-gray-200">
          {title}
        </span>
        <Link
          to={`/compliance/audit?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`}
          className="text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          View full log →
        </Link>
      </div>
      {loading ? (
        <div className="p-3 text-sm text-gray-500">Loading…</div>
      ) : err ? (
        <div className="p-3 text-sm text-red-600 dark:text-red-400">{err}</div>
      ) : rows.length === 0 ? (
        <div className="p-3 text-sm text-muted">
          No audit events recorded for this {entityType}.
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700 text-sm">
          {rows.map((e) => (
            <li key={e.id} className="px-3 py-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-gray-700 dark:text-gray-200">
                  {describe(e)}
                </span>
                <span className="text-xs text-muted whitespace-nowrap">
                  {new Date(e.created_at).toLocaleString()}
                </span>
              </div>
              <div className="text-xs text-muted mt-0.5">
                {e.actor_email || nameFor(e.actor_oid) || 'System'}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
